import React, { useState, useEffect, useRef, useMemo } from "react";
import {
  OutlinedInput,
  MenuItem,
  FormControl,
  ListItemText,
  Select,
  Checkbox,
  Box,
  InputAdornment,
  IconButton,
  TextField,
  ListSubheader,
} from "@mui/material";
import CancelIcon from "@mui/icons-material/Cancel";
import CloseIcon from "@mui/icons-material/Close";
import SearchIcon from "@mui/icons-material/Search";
import { FixedSizeList } from "react-window";
import { useDispatch } from "react-redux";
import { setState } from "../centralAdminSlice";

const ITEM_HEIGHT = 36;
const MAX_VISIBLE_ITEMS = 8;

const MultiSelectCustomerProductCode = (props) => {
  const dispatch = useDispatch();
  const columnData = props?.getSKUData;
  const columns = useMemo(
    () => [...new Set(columnData.map((item) => item.clientProductCode))],
    [columnData]
  );

  const [columnName, setColumnName] = useState([]);
  const [searchText, setSearchText] = useState("");
  const [filteredData, setFilteredData] = useState(columns);
  const [selectAll, setSelectAll] = useState(false);
  const textInput = useRef(null);

  useEffect(() => {
    setFilteredData(columns);
    setColumnName([]);
    setSelectAll(false);
  }, [columns]);

  useEffect(() => {
    const delayDebounceFn = setTimeout(() => {
      if (searchText) {
        const filtered = columns.filter((name) =>
          name.toLowerCase().includes(searchText.toLowerCase())
        );
        setFilteredData(filtered);
      } else {
        setFilteredData(columns);
      }

      setTimeout(() => {
        if (textInput.current) textInput.current.focus();
      }, 100);
    }, 300);

    return () => clearTimeout(delayDebounceFn);
  }, [searchText, columns]);

  useEffect(() => {
    dispatch(setState("selectedClientProductCode", columnName));
  }, [columnName]);

  const handleToggle = (name) => {
    const currentIndex = columnName.indexOf(name);
    const newChecked = [...columnName];

    if (currentIndex === -1) {
      newChecked.push(name);
    } else {
      newChecked.splice(currentIndex, 1);
    }

    setColumnName(newChecked);
  };

  const handleSelectAll = () => {
    if (selectAll) {
      const remaining = columnName.filter((name) => !filteredData.includes(name));
      setColumnName(remaining);
    } else {
      const combined = [...new Set([...columnName, ...filteredData])];
      setColumnName(combined);
    }
    setSelectAll(!selectAll);
  };

  const handleDelete = (e) => {
    e.preventDefault();
    setColumnName([]);
    setSelectAll(false);
  };

  const Row = ({ index, style }) => {
    const name = filteredData[index];
    const checked = columnName.includes(name);
    return (
      <MenuItem key={name} style={style} value={name} onClick={() => handleToggle(name)}>
        <Checkbox checked={checked} />
        <ListItemText primary={name} />
      </MenuItem>
    );
  };

  return (
    <div>
    <FormControl >
      <Select
        multiple
        value={columnName}
        input={<OutlinedInput />}
        size="small"

        sx={{
          width: 300,
          height: "38px !important",
          mt: "-10px !important",
        }}
      
        renderValue={() => (
          <Box sx={{ display: "flex", flexWrap: "wrap", gap: 2 }}>
            <div>
              <span>+ {columnName.length}</span>
              <span>
                <CancelIcon
                  sx={{ paddingLeft: "25px", top: "20%", position: "absolute" }}
                  onMouseDown={(e) => {
                    e.stopPropagation();
                    e.preventDefault();
                  }}
                  onClick={handleDelete}
                />
              </span>
            </div>
          </Box>
        )}
        MenuProps={{
          PaperProps: {
            style: {
              maxHeight: ITEM_HEIGHT * MAX_VISIBLE_ITEMS + 10,
              overflow: "hidden",
            },
          },
        }}
      >
        <MenuItem onClick={handleSelectAll}>
          <Checkbox checked={selectAll} />
          <ListItemText primary="Select All" />
        </MenuItem>

        <ListSubheader>
          <TextField
            size="small"
            inputRef={textInput}
            value={searchText}
            placeholder="Search..."
            onChange={(e) => setSearchText(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon />
                </InputAdornment>
              ),
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton onClick={() => setSearchText("")}>
                    <CloseIcon />
                  </IconButton>
                </InputAdornment>
              ),
            }}
            onKeyDown={(e) => {
              if (e.key !== "Escape") {
                e.stopPropagation();
              }
            }}
          />
        </ListSubheader>

        <FixedSizeList
          height={Math.min(filteredData.length, MAX_VISIBLE_ITEMS) * ITEM_HEIGHT}
          itemCount={filteredData.length}
          itemSize={ITEM_HEIGHT}
          width="100%"
        >
          {Row}
        </FixedSizeList>
      </Select>
    </FormControl>
    </div>
  );
};

export default MultiSelectCustomerProductCode;
